/**
 * ExternalSitePage - "Mam już stronę"
 * Activates CRM, Booster, Recommendations without creating profile
 * Design System P0 compliant - Universal copy (firma/usługi)
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Globe, 
  ArrowRight, Loader2, Sparkles
} from 'lucide-react';
import { API } from '@/utils/api';

export default function ExternalSitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    website_url: '',
    kennel_name: '',
    city: '',
    description: '',
    email: '',
    phone: ''
  });

  const handleSubmit = async () => {
    if (!formData.website_url || !formData.kennel_name) {
      toast.error(t('externalSite.validation'));
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create external entry (minimal profile)
      await axios.post(`${API}/kennels/external`, {
        name: formData.kennel_name,
        website: formData.website_url,
        description: formData.description || `Firma z zewnętrzną stroną: ${formData.website_url}`,
        email: formData.email,
        phone: formData.phone,
        is_external: true,
        species: ['dog']
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('externalSite.success'));
      // Redirect to CRM
      navigate('/panel/leady');
    } catch (err) {
      // Fallback - create regular entry with external flag
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/kennels`, {
          name: formData.kennel_name,
          website: formData.website_url,
          description: formData.description || `Firma z zewnętrzną stroną: ${formData.website_url}`,
          email: formData.email || '',
          phone: formData.phone || '',
          city: formData.city,
          voivodeship: '',
          species: ['dog'],
          breeds: [],
          is_external_site: true
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('externalSite.success'));
        // Redirect to CRM
        navigate('/panel/leady');
      } catch (err2) {
        console.error('Error creating kennel:', err2);
        toast.error(t('externalSite.error'));
        setLoading(false);
      }
    }
  };

  // Benefits with logos
  const benefits = [
    { logo: '/crm-logo.png', title: t('externalSite.benefits.crm'), desc: t('externalSite.benefits.crmDesc') },
    { logo: '/booster-logo.png', title: t('externalSite.benefits.booster'), desc: t('externalSite.benefits.boosterDesc') },
    { logo: '/logo-apisai.png', title: t('externalSite.benefits.recommendations'), desc: t('externalSite.benefits.recommendationsDesc') }
  ];

  return (
    <PanelLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-16 h-16 rounded-2xl bg-[#F7931E]/10 flex items-center justify-center mx-auto mb-4">
            <Globe className="w-8 h-8 text-[#F7931E]" />
          </div>
          <h1 className="text-[28px] font-semibold text-[#0F172A] mb-2">
            {t('externalSite.title')}
          </h1>
          <p className="text-[15px] text-[#475569]">
            {t('externalSite.subtitle')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-[16px] p-6 shadow-[0_4px_12px_rgba(0,0,0,0.04)] border border-[#E5E7EB]"
        >
          {/* Benefits with logos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {benefits.map((benefit, i) => (
              <div key={i} className="p-4 bg-[#F8F6F3] rounded-[12px] text-center">
                <img src={benefit.logo} alt="" className="w-16 h-16 object-contain mx-auto mb-3" />
                <p className="font-medium text-[14px] text-[#0F172A]">{benefit.title}</p>
                <p className="text-[12px] text-[#475569] mt-1">{benefit.desc}</p>
              </div>
            ))}
          </div>

          {/* Motivational sentence */}
          <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#F7931E]/5 to-[#F7931E]/10 rounded-[12px] mb-6 border border-[#F7931E]/20">
            <Sparkles className="w-5 h-5 text-[#F7931E] flex-shrink-0" />
            <p className="text-[14px] text-[#475569]">
              <span className="font-medium text-[#0F172A]">{t('externalSite.motivation')}</span> {t('externalSite.motivationDesc')}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-[14px] font-medium text-[#0F172A]">
                {t('externalSite.form.websiteUrl')} *
              </Label>
              <Input
                type="url"
                placeholder={t('externalSite.form.websitePlaceholder')}
                value={formData.website_url}
                onChange={(e) => setFormData(prev => ({ ...prev, website_url: e.target.value }))}
                className="mt-1.5 h-[44px] bg-white border-[#E5E7EB] rounded-[12px] text-[15px]"
              />
            </div>

            <div>
              <Label className="text-[14px] font-medium text-[#0F172A]">
                {t('externalSite.form.companyName')} *
              </Label>
              <Input
                placeholder={t('externalSite.form.companyPlaceholder')}
                value={formData.kennel_name}
                onChange={(e) => setFormData(prev => ({ ...prev, kennel_name: e.target.value }))}
                className="mt-1.5 h-[44px] bg-white border-[#E5E7EB] rounded-[12px] text-[15px]"
              />
            </div>

            <div>
              <Label className="text-[14px] font-medium text-[#0F172A]">
                Miasto *
              </Label>
              <Input
                placeholder="np. Warszawa, Kraków"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                className="mt-1.5 h-[44px] bg-white border-[#E5E7EB] rounded-[12px] text-[15px]"
              />
            </div>

            <div>
              <Label className="text-[14px] font-medium text-[#0F172A]">
                {t('externalSite.form.email')} <span className="text-[#94A3B8] font-normal">({t('common.optional')})</span>
              </Label>
              <Input
                type="email"
                placeholder={t('externalSite.form.emailPlaceholder')}
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="mt-1.5 h-[44px] bg-white border-[#E5E7EB] rounded-[12px] text-[15px]"
              />
            </div>

            <div>
              <Label className="text-[14px] font-medium text-[#0F172A]">
                {t('externalSite.form.phone')} <span className="text-[#94A3B8] font-normal">({t('common.optional')})</span>
              </Label>
              <Input
                type="tel"
                placeholder={t('externalSite.form.phonePlaceholder')}
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="mt-1.5 h-[44px] bg-white border-[#E5E7EB] rounded-[12px] text-[15px]"
              />
            </div>

            <Button
              onClick={handleSubmit}
              disabled={!formData.website_url || !formData.kennel_name || !formData.city || loading}
              className="w-full h-[48px] bg-[#F7931E] hover:bg-[#E07F0A] rounded-[12px] text-[15px] font-semibold mt-4"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  {t('externalSite.submit')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </div>
    </PanelLayout>
  );
}
